import { hasCollaborationAccess, isEnterpriseTestAccount } from "@/lib/subscriptionAccess";
import { Outlet, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import Sidebar from "@/components/dashboard/Sidebar";
import TopBar from "@/components/dashboard/TopBar";
import AssistFloating from "@/components/dashboard/AssistFloating";
import BuilderModePanel from "@/components/dashboard/BuilderModePanel";
import JuryTour from "@/components/dashboard/JuryTour";
import TrialBanner from "@/components/dashboard/TrialBanner";
import LowCreditsNudge from "@/components/dashboard/LowCreditsNudge";
import TrialExpiredGate from "@/components/dashboard/TrialExpiredGate";
import LockedModule from "@/components/dashboard/LockedModule";
import TestTierSwitcher from "@/components/dashboard/TestTierSwitcher";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { useAuth } from "@/contexts/AuthContext";

// Route → module-slug map used to gate access by the caller's tier.
// Slugs match the keys in `tier.modules` returned by /api/auth/me
// (see backend/tier_catalog.py). Every entry here is a paid-tier
// module — routes not listed (e.g. /dashboard itself, /dashboard/team,
// /dashboard/domains, /dashboard/credits, /dashboard/settings, and the
// four AI assistants) are always accessible when logged in.
const ROUTE_MODULE_SLUG = {
  "/dashboard/planning": "planning",
  "/dashboard/time-tracking": "time_tracking",
  "/dashboard/sales": "sales",
  "/dashboard/finance": "finance",
  "/dashboard/accounting": "accounting",
  "/dashboard/projects": "projects",
  "/dashboard/hr": "hr",
  "/dashboard/operations": "operations",
  "/dashboard/marketing": "marketing",
  "/dashboard/ai-studio": "ai_studio",
  "/dashboard/communication": "communication",
  "/dashboard/compliance": "compliance",
  "/dashboard/canva-studio": "canva",
};

// Routes accessible to trial users while their 24-hour trial is active.
// Everything else renders <LockedModule /> in place of the real page.
const TRIAL_ALLOWED_ROUTES = new Set([
  "/dashboard",
  "/dashboard/",
  "/dashboard/zyntha",
  "/dashboard/thoro",
  "/dashboard/zyona",
  "/dashboard/assist",
  "/dashboard/settings",
]);

// Pretty labels for the locked-module screen.
const MODULE_LABELS = {
  "/dashboard/hr": "HR",
  "/dashboard/accounting": "Accounting",
  "/dashboard/communication": "Communication",
  "/dashboard/compliance": "Compliance",
  "/dashboard/finance": "Finance & Invoicing",
  "/dashboard/sales": "Sales",
  "/dashboard/projects": "Projects",
  "/dashboard/planning": "Planning",
  "/dashboard/time-tracking": "Time Tracking",
  "/dashboard/operations": "Operations",
  "/dashboard/canva-studio": "Canva Studio",
  "/dashboard/marketing": "Marketing & Content",
  "/dashboard/ai-studio": "AI Studio",
  "/dashboard/team": "Team",
  "/dashboard/meetings": "Meetings & Calendar",
  "/dashboard/recordings": "Recordings & Reports",
};

function trialState(user) {
  if (isEnterpriseTestAccount(user)) return "not_trial";
  // A paid account must NEVER be treated as a trial, even if a stale
  // `is_trial=true` sneaks in from a delayed webhook. Any of these
  // signals means the user has already paid:
  //   - subscription_plan starts with a paid tier label / tier_key
  //   - subscription_status === "active"
  //   - is_lifetime flag set (Kickstart)
  //   - has a Stripe subscription id or Stripe customer id
  //   - tier.plan_key resolves to a paid tier
  const plan = String(user?.subscription_plan || "").toLowerCase();
  const tierKey = String(user?.tier?.plan_key || "").toLowerCase();
  const status = String(user?.subscription_status || "").toLowerCase();
  const isPaidPlanName = (s) =>
    s.startsWith("kickstart") ||
    s.startsWith("starter") ||
    s.startsWith("enterprise") ||
    s.startsWith("basis") ||
    s.startsWith("plus") ||
    s.startsWith("site basis") ||
    s.startsWith("site plus");
  const paidPlan = isPaidPlanName(plan) || isPaidPlanName(tierKey);
  if (
    paidPlan ||
    status === "active" ||
    user?.is_lifetime ||
    user?.stripe_subscription_id ||
    user?.stripe_customer_id ||
    user?.is_founder ||
    user?.is_demo ||
    user?.is_unlimited ||
    user?.billing_exempt
  ) {
    return "not_trial";
  }
  if (user?.is_trial !== true) return "not_trial";
  const exp = user.trial_expires_at;
  if (!exp) return "expired";
  const end = new Date(exp).getTime();
  if (Number.isNaN(end)) return "expired";
  return Date.now() < end ? "active" : "expired";
}

export default function DashboardLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState("user");
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const workspaceUser = useMemo(() => {
    if (!user?.workspace_context) return user;
    return {
      ...user,
      ...user.workspace_context,
      is_founder: ownWorkspace && user.is_founder,
      is_demo: ownWorkspace && user.is_demo,
      is_unlimited: ownWorkspace && user.is_unlimited,
      billing_exempt: ownWorkspace && user.billing_exempt,
      stripe_subscription_id: ownWorkspace ? user.stripe_subscription_id : null,
      stripe_customer_id: ownWorkspace ? user.stripe_customer_id : null,
    };
  }, [user, ownWorkspace]);
  const builder = mode === "builder" && workspaceUser?.is_founder;

  const tState = useMemo(() => trialState(workspaceUser), [workspaceUser]);

  // Hard-block: trial has run out. Replace the whole dashboard with the
  // expired gate — no sidebar, no topbar, no accidentally-lingering
  // module content.
  if (tState === "expired") {
    return <TrialExpiredGate ownWorkspace={ownWorkspace} />;
  }

  const isTrialActive = tState === "active";
  const pathname = location.pathname.replace(/\/+$/, "") || "/dashboard";
  const isAllowedForTrial = TRIAL_ALLOWED_ROUTES.has(pathname) || TRIAL_ALLOWED_ROUTES.has(pathname + "/");
  const showLockedModule = isTrialActive && !isAllowedForTrial;

  // Tier-based route gate (paid users). Founder / demo / unlimited /
  // billing-exempt bypass the gate entirely — everyone else must have
  // the route's module slug in their `tier.modules` allow-list.
  const isPrivileged = !!(
    workspaceUser?.is_founder || workspaceUser?.is_unlimited ||
    workspaceUser?.billing_exempt || workspaceUser?.is_demo
  );
  const routeSlug = ROUTE_MODULE_SLUG[pathname];
  const allowedModules = workspaceUser?.tier?.modules;
  const tierBlocked =
    !isTrialActive &&
    !isPrivileged &&
    !!routeSlug &&
    Array.isArray(allowedModules) &&
    !allowedModules.includes(routeSlug);
  const subscriptionRoutes = new Set(["/dashboard/meetings", "/dashboard/recordings"]);
  const subscriptionEligible = hasCollaborationAccess(workspaceUser);
  const subscriptionBlocked = subscriptionRoutes.has(pathname) && !subscriptionEligible;

  // Which Kickstart tier is the user on? Drives the upsell copy.
  const planKey = String(workspaceUser?.tier?.plan_key || workspaceUser?.subscription_plan || "");
  const tierReason = planKey.startsWith("Kickstart 1")
    ? "kickstart_1"
    : planKey.startsWith("Kickstart 2")
    ? "kickstart_2"
    : planKey.startsWith("Kickstart 3")
    ? "kickstart_3"
    : "kickstart_locked";

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFB]">
      {isTrialActive && <TrialBanner user={workspaceUser} canManagePlan={ownWorkspace} />}
      <div className="flex-1 flex min-h-0">
        <Sidebar
          user={workspaceUser}
          mode={mode}
          onToggleMode={() => setMode((m) => (m === "user" ? "builder" : "user"))}
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <TopBar />
          <main
            className="flex-1 px-3 sm:px-6 lg:px-8 py-6 sm:py-8"
            data-testid="dashboard-main"
          >
            {showLockedModule ? (
              <LockedModule moduleName={MODULE_LABELS[pathname]} reason="trial" />
            ) : subscriptionBlocked ? (
              <LockedModule moduleName={MODULE_LABELS[pathname]} reason="kickstart_locked" />
            ) : tierBlocked ? (
              <LockedModule moduleName={MODULE_LABELS[pathname]} reason={tierReason} />
            ) : (
              <RouteErrorBoundary>
                <Outlet context={{ mode }} />
              </RouteErrorBoundary>
            )}
            {builder && <BuilderModePanel />}
          </main>
        </div>
        <AssistFloating />
        <JuryTour />
        <LowCreditsNudge />
        <TestTierSwitcher />
      </div>
    </div>
  );
}
